import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  InputBase,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import {
  BOT_FEATURE_LABELS,
  CALL_FEATURE_LABELS,
  CHAT_FEATURE_LABELS,
  FONT_OPTIONS,
  TAB_LABELS,
  TabId,
  WidgetBuilderConfig,
  WidgetColors,
  WidgetFontSize,
  WidgetThemeMode,
} from '../../types/widgetBuilder';
import SidebarDropdown from './SidebarDropdown';

type Props = {
  config: WidgetBuilderConfig;
  onChange: (next: WidgetBuilderConfig) => void;
};

type NavTabId = 'tabs' | 'chat_features' | 'call_features' | 'bot_features' | 'style';

const COLOR_PRESETS = ['#007AFF', '#7C3AED', '#14B8A6', '#F87171', '#1E3A5F'] as const;

const HOST_CALL_CONTROL_KEYS: Array<(typeof CALL_FEATURE_LABELS)[number]['key']> = [
  'is_microphone_control',
  'is_remove_participants',
  'is_make_host_call',
];

const FONT_DESCRIPTIONS: Record<string, string> = {
  Poppins: 'clean and familiar',
  Roboto: 'neutral and readable',
  Inter: 'modern and clear',
  'Open Sans': 'friendly and open',
  Nunito: 'soft and rounded',
};

const TEXT_SIZE_OPTIONS: Array<{ value: WidgetFontSize; label: string }> = [
  { value: 'compact', label: 'Compact' },
  { value: 'default', label: 'Default' },
  { value: 'comfortable', label: 'Comfortable' },
];

const COLOR_FIELDS: Array<{ key: keyof WidgetColors; label: string }> = [
  { key: 'brand_color', label: 'Brand color' },
  { key: 'primary_text_light', label: 'Primary color' },
  { key: 'secondary_text_light', label: 'Secondary color' },
  { key: 'primary_text_dark', label: 'Primary text (dark)' },
  { key: 'secondary_text_dark', label: 'Secondary text (dark)' },
];

const normalizeHex = (value: string) => value.trim().toLowerCase();

const ColorFieldBlock: React.FC<{
  label: string;
  value: string;
  onChange: (next: string) => void;
  showDivider?: boolean;
}> = ({ label, value, onChange, showDivider = true }) => {
  const current = normalizeHex(value);
  const colorInputRef = React.useRef<HTMLInputElement>(null);

  return (
    <Box
      sx={{
        pb: 2,
        mb: 2,
        borderBottom: showDivider ? '1px solid #E8EEF5' : 'none',
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 13.5,
          color: '#0F172A',
          textAlign: 'left',
          mb: 1.25,
        }}
      >
        {label}
      </Typography>

      <Stack direction="row" spacing={1.25} sx={{ mb: 1.5 }}>
        {COLOR_PRESETS.map((preset) => {
          const selected = current === normalizeHex(preset);
          return (
            <Box
              key={preset}
              onClick={() => onChange(preset)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: preset,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: selected ? '2px solid #007AFF' : '1.5px solid rgba(15, 23, 42, 0.12)',
                boxShadow: selected ? '0 0 0 2px rgba(0, 122, 255, 0.18)' : 'none',
                flexShrink: 0,
              }}
            >
              {selected && <CheckIcon sx={{ fontSize: 14, color: '#FFFFFF' }} />}
            </Box>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontSize: 12.5, fontWeight: 400, color: '#64748B', textAlign: 'left', flex: 1 }}>
          Custom {label.toLowerCase()}
        </Typography>
        <Box
          onClick={() => colorInputRef.current?.click()}
          sx={{
            width: 22,
            height: 22,
            borderRadius: 0.75,
            backgroundColor: value || '#007AFF',
            border: '1px solid rgba(15, 23, 42, 0.12)',
            cursor: 'pointer',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <input
            ref={colorInputRef}
            type="color"
            value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#007AFF'}
            onChange={(e) => onChange(e.target.value)}
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0,
              cursor: 'pointer',
              border: 0,
              padding: 0,
              width: '100%',
              height: '100%',
            }}
          />
        </Box>
        <InputBase
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={{
            width: 78,
            fontSize: 12.5,
            fontWeight: 400,
            color: '#0F172A',
            '& input': { py: 0, px: 0 },
          }}
        />
      </Stack>
    </Box>
  );
};

const featureSwitchSx = {
  flexShrink: 0,
  ml: 1.5,
  width: 28,
  height: 16,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '2px',
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#ffffff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#007AFF',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 12,
    height: 12,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.16)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 8,
    backgroundColor: '#CBD5E1',
    opacity: 1,
  },
};

const FeatureRow: React.FC<{
  checked: boolean;
  label: string;
  onToggle: () => void;
}> = ({ checked, label, onToggle }) => (
  <Box
    sx={{
      width: '100%',
      m: 0,
      px: 0,
      py: 1.1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      backgroundColor: 'transparent',
    }}
  >
    <Typography
      sx={{
        minWidth: 0,
        flex: 1,
        fontWeight: 400,
        fontSize: 14,
        color: '#1E293B',
        lineHeight: 1.4,
        textAlign: 'left',
      }}
    >
      {label}
    </Typography>
    <Switch checked={checked} onChange={onToggle} disableRipple sx={featureSwitchSx} />
  </Box>
);

const headingSx = {
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: '#0F172A',
  textAlign: 'left' as const,
};

const SectionHeading: React.FC<{ title: string; description?: string }> = ({ title, description }) => (
  <Box
    sx={{
      mb: 2.5,
      pt: 0.5,
      pb: 2,
      textAlign: 'left',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      borderBottom: '1px solid #E8EEF5',
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: '#007AFF',
        textAlign: 'left',
        lineHeight: 1.3,
        mb: 0.75,
      }}
    >
      Configuration
    </Typography>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: 22,
        letterSpacing: '-0.02em',
        color: '#0F172A',
        textAlign: 'left',
        lineHeight: 1.25,
        mb: description ? 1 : 0,
      }}
    >
      {title}
    </Typography>
    {description && (
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 13.5,
          color: '#64748B',
          textAlign: 'left',
          lineHeight: 1.45,
        }}
      >
        {description}
      </Typography>
    )}
  </Box>
);

const LayoutSidebar: React.FC<Props> = ({ config, onChange }) => {
  const navItems = useMemo(() => {
    if (config.is_bot_varient) {
      return [
        { id: 'bot_features' as const, label: 'Bot Features', icon: <AutoAwesomeOutlinedIcon fontSize="small" /> },
        { id: 'style' as const, label: 'Style', icon: <PaletteOutlinedIcon fontSize="small" /> },
      ];
    }
    return [
      { id: 'tabs' as const, label: 'Tabs', icon: <DashboardCustomizeOutlinedIcon fontSize="small" /> },
      { id: 'chat_features' as const, label: 'Chat Features', icon: <ChatBubbleOutlineIcon fontSize="small" /> },
      { id: 'call_features' as const, label: 'Call Features', icon: <CallOutlinedIcon fontSize="small" /> },
      { id: 'style' as const, label: 'Style', icon: <PaletteOutlinedIcon fontSize="small" /> },
    ];
  }, [config.is_bot_varient]);

  const [activeTab, setActiveTab] = useState<NavTabId>(
    config.is_bot_varient ? 'bot_features' : 'tabs'
  );

  useEffect(() => {
    const fallback = (config.is_bot_varient ? 'bot_features' : 'tabs') as NavTabId;
    if (!navItems.some((item) => item.id === activeTab)) {
      setActiveTab(fallback);
    }
  }, [config.is_bot_varient, navItems, activeTab]);

  const currentTab = navItems.some((item) => item.id === activeTab)
    ? activeTab
    : navItems[0].id;

  const toggleTab = (id: TabId) => {
    onChange({
      ...config,
      tabs: {
        ...config.tabs,
        [id]: !config.tabs[id],
      },
    });
  };

  const renderContent = () => {
    switch (currentTab) {
      case 'tabs':
        return (
          <>
            <SectionHeading
              title="Layout"
              description="Choose how people enter and move through chat."
            />
            
            <Box
              sx={{
                mb: 2.5,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                <Typography sx={{ fontWeight: 400, fontSize: 13.5, color: '#0F172A', textAlign: 'left' }}>
                  Show chat navigation
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.35, textAlign: 'left' }}>
                  Quick access to chats, calls, people, and groups.
                </Typography>
              </Box>
              <Switch
                checked={config.with_sidebar}
                onChange={() => onChange({ ...config, with_sidebar: !config.with_sidebar })}
                sx={featureSwitchSx}
              />
            </Box>

            <Typography sx={{ ...headingSx, mb: 1 }}>
              Tabs
            </Typography>
            <Box>
              {TAB_LABELS.map((tab) => (
                <FeatureRow
                  key={tab.id}
                  checked={config.tabs[tab.id]}
                  label={tab.label}
                  onToggle={() => toggleTab(tab.id)}
                />
              ))}
            </Box>
          </>
        );

      case 'chat_features':
        return (
          <>
            <SectionHeading
              title="Chat Features"
              description="Enable messaging capabilities shown in the live preview."
            />
            <Box>
              {CHAT_FEATURE_LABELS.map((item) => (
                <FeatureRow
                  key={item.key}
                  checked={config.chat_feature[item.key]}
                  label={item.label}
                  onToggle={() =>
                    onChange({
                      ...config,
                      chat_feature: {
                        ...config.chat_feature,
                        [item.key]: !config.chat_feature[item.key],
                      },
                    })
                  }
                />
              ))}
            </Box>
          </>
        );

      case 'call_features':
        return (
          <>
            <SectionHeading
              title="Call Features"
              description="Control voice and video calling options in the preview."
            />
            <Box>
              {CALL_FEATURE_LABELS.filter((item) => !HOST_CALL_CONTROL_KEYS.includes(item.key)).map((item) => (
                <FeatureRow
                  key={item.key}
                  checked={config.call_feature[item.key]}
                  label={item.label}
                  onToggle={() =>
                    onChange({
                      ...config,
                      call_feature: {
                        ...config.call_feature,
                        [item.key]: !config.call_feature[item.key],
                      },
                    })
                  }
                />
              ))}
            </Box>
            <Typography
              sx={{
                mt: 1.5,
                mb: 0.5,
                pt: 1.5,
                borderTop: '1px solid #E8EEF5',
                fontWeight: 700,
                fontSize: 13,
                color: '#0F172A',
                textAlign: 'left',
              }}
            >
              Host call Controls
            </Typography>
            <Box>
              {HOST_CALL_CONTROL_KEYS.map((key) => {
                const item = CALL_FEATURE_LABELS.find((entry) => entry.key === key);
                if (!item) return null;
                return (
                  <FeatureRow
                    key={item.key}
                    checked={config.call_feature[item.key]}
                    label={item.label}
                    onToggle={() =>
                      onChange({
                        ...config,
                        call_feature: {
                          ...config.call_feature,
                          [item.key]: !config.call_feature[item.key],
                        },
                      })
                    }
                  />
                );
              })}
            </Box>
          </>
        );

      case 'bot_features':
        return (
          <>
            <SectionHeading
              title="Bot Features"
              description="Configure chatbot session and agent options."
            />
            <Box>
              {BOT_FEATURE_LABELS.map((item) => (
                <FeatureRow
                  key={item.key}
                  checked={config.bot_features[item.key]}
                  label={item.label}
                  onToggle={() =>
                    onChange({
                      ...config,
                      bot_features: {
                        ...config.bot_features,
                        [item.key]: !config.bot_features[item.key],
                      },
                    })
                  }
                />
              ))}
            </Box>
          </>
        );

      case 'style':
        return (
          <>
            <SectionHeading
              title="Style"
              description="Theme, colors, and typography for the live preview."
            />

            <Typography sx={{ ...headingSx, mb: 1 }}>
              Theme
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
              {([
                { id: 'system', label: 'System' },
                { id: 'light', label: 'Light' },
                { id: 'dark', label: 'Dark' },
              ] as Array<{ id: WidgetThemeMode; label: string }>).map((item) => {
                const selected = config.theme === item.id;
                return (
                  <Box
                    key={item.id}
                    onClick={() => onChange({ ...config, theme: item.id })}
                    sx={{
                      flex: 1,
                      cursor: 'pointer',
                      borderRadius: 2,
                      border: '1.5px solid',
                      borderColor: selected ? '#007AFF' : '#e0e0e0',
                      backgroundColor: selected ? 'rgba(0,122,255,0.06)' : '#ffffff',
                      p: 1,
                      boxShadow: selected ? '0 0 0 3px rgba(0,122,255,0.1)' : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        height: 36,
                        borderRadius: 1.5,
                        mb: 0.75,
                        border: '1px solid #e0e0e0',
                        background:
                          item.id === 'dark'
                            ? 'linear-gradient(135deg, #0F172A 0%, #334155 100%)'
                            : item.id === 'light'
                              ? 'linear-gradient(135deg, #FFFFFF 0%, #F1F5F9 100%)'
                              : 'linear-gradient(90deg, #FFFFFF 50%, #0F172A 50%)',
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: 11.5,
                        color: selected ? '#007AFF' : '#334155',
                        textAlign: 'left',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 13.5,
                color: '#0F172A',
                textAlign: 'left',
                mb: 1.25,
              }}
            >
              Typography
            </Typography>
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2.5,
                backgroundColor: '#F4F7FB',
                border: '1px solid #E8EEF5',
                textAlign: 'left',
              }}
            >
              <Typography
                sx={{
                  fontFamily: `"${config.typography.font}", sans-serif`,
                  fontWeight: 700,
                  fontSize: 36,
                  lineHeight: 1,
                  color: config.colors.brand_color || '#007AFF',
                  mb: 0.75,
                }}
              >
                Aa
              </Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 400, color: '#64748B' }}>
                {config.typography.font} ·{' '}
                {FONT_DESCRIPTIONS[config.typography.font] || 'clean and familiar'}
              </Typography>
            </Box>
            <Stack
              spacing={1.5}
              sx={{
                mb: 2.5,
                pb: 2.5,
                borderBottom: '1px solid #E8EEF5',
              }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A', mb: 0.75, textAlign: 'left' }}
                >
                  Font family
                </Typography>
                <SidebarDropdown
                  value={config.typography.font}
                  options={FONT_OPTIONS.map((font) => ({
                    value: font,
                    label: font,
                    labelSx: { fontFamily: `"${font}", sans-serif` },
                  }))}
                  onChange={(font) =>
                    onChange({
                      ...config,
                      typography: { ...config.typography, font },
                    })
                  }
                />
              </Box>
              <Box>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A', mb: 0.75, textAlign: 'left' }}
                >
                  Text size
                </Typography>
                <SidebarDropdown
                  value={config.typography.size}
                  options={TEXT_SIZE_OPTIONS}
                  onChange={(size) =>
                    onChange({
                      ...config,
                      typography: {
                        ...config.typography,
                        size: size as WidgetFontSize,
                      },
                    })
                  }
                />
              </Box>
            </Stack>

            <Typography sx={{ ...headingSx, mb: 1.5 }}>
              Colors
            </Typography>
            <Box sx={{ mb: 1 }}>
              {COLOR_FIELDS.map((field, index) => (
                <ColorFieldBlock
                  key={field.key}
                  label={field.label}
                  value={config.colors[field.key]}
                  onChange={(next) =>
                    onChange({
                      ...config,
                      colors: { ...config.colors, [field.key]: next },
                    })
                  }
                  showDivider={index < COLOR_FIELDS.length - 1}
                />
              ))}
            </Box>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        minHeight: 0,
        textAlign: 'left',
        backgroundColor: '#ffffff',
      }}
    >
      <Box
        sx={{
          width: 56,
          flexShrink: 0,
          alignSelf: 'stretch',
          borderRight: '1px solid #e0e0e0',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          py: 1.5,
          gap: 0.75,
        }}
      >
        {navItems.map((item) => {
          const selected = currentTab === item.id;
          const isStyle = item.id === 'style';
          return (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0,
                width: '100%',
              }}
            >
              {isStyle && (
                <Box
                  sx={{
                    width: 28,
                    height: '1px',
                    mb: 0.75,
                    flexShrink: 0,
                    backgroundColor: '#e0e0e0',
                  }}
                />
              )}
              <Tooltip title={item.label} placement="right">
                <IconButton
                  onClick={() => setActiveTab(item.id)}
                  sx={{
                    width: 40,
                    height: 40,
                    minHeight: 40,
                    maxHeight: 40,
                    flexShrink: 0,
                    borderRadius: 2,
                    color: selected ? '#007AFF' : '#64748B',
                    backgroundColor: selected ? 'rgba(0, 122, 255, 0.12)' : 'transparent',
                    border: selected ? '1px solid rgba(0, 122, 255, 0.25)' : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: selected
                        ? 'rgba(0, 122, 255, 0.16)'
                        : 'rgba(15, 23, 42, 0.04)',
                    },
                  }}
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: 'auto',
          px: 2.25,
          pt: 2.75,
          pb: 2.5,
        }}
      >
        <Box key={currentTab}>{renderContent()}</Box>
      </Box>
    </Box>
  );
};

export default LayoutSidebar;
